import { useEffect, useState } from 'react';
import {
  api, type DemandeVersement, type EtatVersement, type LigneVersement,
  type PartieAffaire, type Reconnaissance, type VersementRecuApi,
} from './api';

/**
 * VERSEMENT D'IDENTITÉ — l'agent demande à Mowibo ID, la personne consent, le
 * registre reçoit les attributs ET les fichiers.
 *
 * ⚠️ TROIS CANAUX, ET LE CODE DICTÉ EST LE PLUS GRAND À L'ÉCRAN. « il faut le
 * lien aussi, ou un code otp, c'est compliqué lire un qrcode depuis un pc
 * burautique » — le fondateur, 03/10/2026, après qu'un guichet n'eut été livré
 * qu'en QR. Un agent derrière un poste bureautique n'a pas de caméra : le code
 * se dicte, le QR sert au téléphone, le lien part par courriel.
 *
 * ⚠️ LE NOM VERSÉ EST ENCADRÉ, PARCE QU'IL EST LE SEUL CONTRÔLE HUMAIN. Rien
 * n'empêche techniquement qu'une AUTRE personne ait autorisé la demande : ni le
 * code, ni le lien ne prouvent qui a tapé. L'agent compare le nom reçu à celui
 * qu'il a devant lui — donc ce nom doit sauter aux yeux, pas se fondre dans un
 * tableau.
 *
 * ⚠️ « VERSÉ » EST DÉFINITIF, ET L'ÉCRAN LE DIT AVANT. Le retrait du partage
 * chez Mowibo ID ne retire pas les pièces du dossier : un acte foncier est
 * opposable aux tiers des décennies après sa date. Taire ce point ferait
 * consentir sur une information inexacte.
 */
export function SectionVersementIdentite({ reference, onChange }: {
  reference: string;
  onChange?: () => void;
}) {
  const [etat, setEtat] = useState<EtatVersement | null>(null);
  const [lignes, setLignes] = useState<LigneVersement[] | null>(null);
  const [demande, setDemande] = useState<DemandeVersement | null>(null);
  const [recu, setRecu] = useState<VersementRecuApi | null>(null);
  const [titulaireId, setTitulaireId] = useState('');
  const [motif, setMotif] = useState('');
  const [parties, setParties] = useState<PartieAffaire[] | null>(null);
  const [rolesDeclarables, setRolesDeclarables] = useState<Array<{ role: string; libelle: string }>>([]);
  const [enrolement, setEnrolement] = useState<{ lien: string; aDire: string } | null>(null);
  const [ajoutRole, setAjoutRole] = useState('');
  const [ajoutNom, setAjoutNom] = useState('');
  /**
   * ⚠️ LE NUMÉRO NE RESTE PAS. Il sert à UN appel puis le champ est vidé : un
   * NIN qui séjourne dans l'état d'un écran de guichet survit à la personne qui
   * est partie, et se retrouve dans le prochain rapport d'erreur du navigateur.
   */
  const [nin, setNin] = useState('');
  const [reco, setReco] = useState<Reconnaissance | null>(null);
  const [rattachementADire, setRattachementADire] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const recharger = () => {
    api.versements.deLAffaire(reference)
      .then((r) => setLignes(r.versements))
      .catch((e) => setErreur(String((e as Error)?.message ?? e)));
    api.versements.parties(reference)
      .then((r) => { setParties(r.parties); setRolesDeclarables(r.rolesDeclarables ?? []); })
      .catch(() => setParties(null)); // l'union absente se DIT plus bas, elle ne casse pas le guichet
  };

  useEffect(() => {
    setDemande(null); setRecu(null); setErreur(null);
    api.versements.etat().then(setEtat).catch(() => setEtat(null));
    recharger();
  }, [reference]);

  /**
   * ⚠️ LE SUIVI EN DIRECT (refonte D.1519) : tant qu'un enrôlement est en
   * route (pas de décision, pas versé), la liste se recharge toute seule —
   * l'agent voit le dossier avancer sans toucher à rien. Le pas est lent
   * exprès : six secondes suffisent à un guichet, et chaque recharge
   * interroge Mowibo ID pour chaque enrôlement vivant.
   */
  const enAttente = (parties ?? []).some((p) =>
    p.enrolement && p.enrolement.suivi?.decision !== 'approuve'
    && p.enrolement.suivi?.decision !== 'rejete'
    && (!p.versement || p.versement.statut !== 'verse'));
  useEffect(() => {
    if (!enAttente) return;
    const t = setInterval(recharger, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enAttente, reference]);

  /* ⚠️ « FERMÉ » N'EST PAS « CASSÉ » — et l'écran le DIT, au lieu d'afficher un
     bouton qui rend 501. Un agent devant un bouton qui échoue ouvre un
     incident ; un agent devant une phrase qui explique emploie le guichet. */
  if (etat && !etat.disponible) {
    return (
      <section className="piece-versement-identite">
        <h4>Identité par Mowibo ID</h4>
        <p className="note-indispo">
          {etat.pourquoi ?? "Le versement Mowibo ID n'est pas configuré sur cette instance."}
        </p>
      </section>
    );
  }

  const demanderVersement = async () => {
    setErreur(null); setRecu(null); setEnCours(true);
    try {
      setDemande(await api.versements.demander(reference, { titulaireId, motif }));
    } catch (e) {
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  /**
   * ⚠️ LA DEMANDE PART DE LA PARTIE, PLUS D'UN UUID COLLÉ. Avant D.1479,
   * l'agent devait retrouver l'identifiant du titulaire à la main : les
   * témoins — proposés nulle part — n'étaient JAMAIS versés. Le motif est
   * composé d'office : c'est ce que la personne lira sur son écran de
   * consentement, et il NOMME la qualité au titre de laquelle on l'identifie.
   */
  /**
   * ⚠️ L'ENRÔLEMENT AU GUICHET — pour la partie qui N'A PAS de compte Mowibo ID
   * (D.1506). Demander un versement à quelqu'un qui ne peut pas consentir
   * mènerait à un code que personne n'honorera : ce geste-ci ouvre la porte de
   * VÉRIFICATION, l'agent capture pièce + puce + portrait dans l'app, le compte
   * naît à l'approbation — et le versement par rôle devient possible ici même.
   */
  const enrolerPartie = async (x: PartieAffaire) => {
    setErreur(null); setEnrolement(null); setEnCours(true);
    try {
      // ⚠️ Une partie DÉCLARÉE s'enrôle aussi — c'est même le cas nominal :
      // l'enrôlement vise qui n'est encore nulle part (constat du fondateur,
      // 15/10 : « je ne vois aucun endroit où l'on demande les documents »).
      const r = await api.versements.enroler(reference, {
        role: x.role,
        titulaireId: x.titulaire?.id,
        nomDeclare: x.nomDeclare ?? undefined,
      });
      setEnrolement({ lien: r.lien, aDire: r.aDire });
      recharger();
    } catch (e) {
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  /** « La pièce d'abord » : déclarer une partie NEUVE et l'enrôler, un geste. */
  const declarerEtEnroler = async () => {
    if (!ajoutRole) return;
    setErreur(null); setEnrolement(null); setEnCours(true);
    try {
      const r = await api.versements.enroler(reference, {
        role: ajoutRole,
        nomDeclare: ajoutNom.trim() || undefined,
      });
      setEnrolement({ lien: r.lien, aDire: r.aDire });
      setAjoutRole(''); setAjoutNom('');
      recharger();
    } catch (e) {
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  /**
   * CHERCHER AVANT DE CRÉER (D.1519, ligne rouge n° 3). L'agent a la carte en
   * main : avant toute capture, on demande au registre s'il connaît déjà cette
   * personne. Le verdict vient du SERVEUR — l'écran n'en décide aucune règle.
   */
  const reconnaitre = async () => {
    const numero = nin.trim();
    if (!numero) return;
    setErreur(null); setReco(null); setRattachementADire(null); setEnCours(true);
    try {
      const r = await api.versements.reconnaitre(reference, numero);
      setReco(r);
      // ⚠️ VIDÉ DÈS L'APPEL PARTI, succès ou échec : voir `nin` ci-dessus.
      setNin('');
    } catch (e) {
      setNin('');
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  /**
   * Sa fiche existe : on la rattache, on n'en crée pas une seconde. Le
   * versement est demandé dans le même geste quand l'identité est établie —
   * c'est tout l'intérêt : la PIÈCE n'est pas relue, seul le CONSENTEMENT à
   * cette affaire-ci est redemandé.
   */
  const rattacherReconnu = async () => {
    if (!reco?.titulaire || !ajoutRole) return;
    setErreur(null); setRattachementADire(null); setEnCours(true);
    try {
      const r = await api.versements.rattacher(reference, {
        role: ajoutRole,
        titulaireId: reco.titulaire.id,
        demanderIdentite: reco.verdict === 'rattache_existant',
      });
      setRattachementADire(r.aDire);
      setReco(null); setAjoutRole('');
      recharger();
    } catch (e) {
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  const demanderPourPartie = async (x: PartieAffaire) => {
    if (!x.titulaire) return;
    setErreur(null); setRecu(null); setEnCours(true);
    try {
      setDemande(await api.versements.demander(reference, {
        titulaireId: x.titulaire.id,
        motif: `Instruction de l'affaire : identité — ${x.libelle}`,
        rolePartie: x.role,
      }));
    } catch (e) {
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  const recevoir = async (id: string) => {
    setErreur(null); setEnCours(true);
    try {
      const r = await api.versements.recevoir(id);
      setRecu(r); setDemande(null); recharger(); onChange?.();
    } catch (e) {
      setErreur(String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <section className="piece-versement-identite">
      <h4>Identité par Mowibo ID</h4>

      <p className="mention">
        Vaut pour <strong>tout intervenant</strong> de l'affaire — requérant, cédant,
        acquéreur, héritier, témoin, mandataire — quel que soit le type de demande ou
        d'opération. La personne autorise sur son téléphone ; le registre reçoit son
        état civil et ses pièces, et <strong>les conserve</strong>.
      </p>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ── La demande ─────────────────────────────────────────────────── */}
      {!demande && (
        <div className="versement-demande">
          {/* ⚠️⚠️ LES PARTIES DE L'ACTE, PAR RÔLE — TÉMOINS COMPRIS (D.1479).
              L'union vient du service (composition + attestation + succession
              + requérant) : l'écran ne récite aucun vocabulaire. */}
          {parties === null && (
            <p className="note-indispo">
              La liste des parties n'a pas pu être lue — le chemin manuel ci-dessous reste entier.
            </p>
          )}
          {/* ═══════════════════════════════════════════════════════════════
              L'ASSISTANT « CONSTITUER LES PARTIES » — refonte D.1519,
              mandatée le 16/10 (« je ne vois rien de ce qui a été fait »).
              La pièce d'abord : chaque partie est UNE ÉTAPE, son état avance
              tout seul (suivi en direct), ses constats se pré-remplissent —
              ÉTIQUETÉS, jamais donnés pour vérité. Trois lignes rouges tenues :
              pré-rempli ≠ vérifié · on cherche avant de créer · le consentement
              précède la lecture (le serveur refuse sinon).
              ═══════════════════════════════════════════════════════════════ */}
          {parties && parties.length === 0 && (
            <p className="attente">
              Aucune partie connue de cette affaire pour l'instant — déclarez la
              première ci-dessous : son rôle, puis sa pièce.
            </p>
          )}
          {parties && parties.length > 0 && (
            <ul className="liste-parties">
              {parties.map((x, i) => {
                const verse = x.versement?.statut === 'verse';
                const e = x.enrolement;
                const s = e?.suivi ?? null;
                const constats = s ? Object.entries(s.champs) : [];
                return (
                  <li key={`${x.role}:${x.titulaire?.id ?? x.nomDeclare ?? ''}`}>
                    <strong>Étape {i + 1}/{parties.length} — {x.libelle}</strong>
                    {' — '}
                    {x.titulaire
                      ? x.titulaire.denomination
                      : (x.nomDeclare ?? <em>personne non encore désignée</em>)}
                    {x.piecesAuDossier > 0 && <> · {x.piecesAuDossier} pièce(s) à ce rôle</>}

                    {/* ── l'état de l'étape, UNE phrase qui dit où on en est ── */}
                    {verse && <p className="mention">✓ Identité versée au dossier, à ce rôle.</p>}
                    {!verse && x.versement && x.versement.statut !== 'verse' && (
                      <p className="attente">
                        Demande de versement « {x.versement.statut} » — le consentement se
                        recueille dans la console Mowibo ID (Partages → Consentir au guichet),
                        puis « Encaisser » ci-dessous.
                      </p>
                    )}
                    {!verse && !x.versement && e && e.rattachement && (
                      <p className="mention">
                        ✓ Rattachée depuis le registre — sa fiche existait, aucune capture de
                        pièce n'a été refaite.
                        {x.versement ? '' : " Il reste son consentement au versement dans cette affaire."}
                      </p>
                    )}
                    {!verse && !x.versement && e && !e.rattachement && (
                      s === null ? (
                        <p className="note-indispo">
                          Suivi indisponible : {e.suiviIndisponible ?? 'Mowibo ID n\'a pas répondu'} —
                          le lien d'enrôlement reste valable.
                        </p>
                      ) : s.decision === 'approuve' ? (
                        <p className="mention">
                          ✓ Dossier approuvé en présence — le compte de la personne est né.
                          {x.titulaire
                            ? ' Demandez son identité ci-dessous pour la verser au dossier.'
                            : ' Le versement au dossier attendra son inscription comme titulaire (l\'acte l\'inscrira).'}
                        </p>
                      ) : s.decision === 'rejete' ? (
                        <p className="erreur">Dossier rejeté{s.motifRejet ? ` : ${s.motifRejet}` : ''}.</p>
                      ) : s.statut === 'deposee' ? (
                        <p className="attente">Pièces déposées, dossier complet — à approuver dans la
                          console Mowibo ID (constat visuel, la personne devant vous).</p>
                      ) : s.statut === 'ouverte' ? (
                        <p className="attente">Consentement donné — dépôt des pièces en cours sur le téléphone…</p>
                      ) : s.statut === 'envoyee' ? (
                        <p className="attente">Lien émis — en attente d'ouverture sur le téléphone.</p>
                      ) : (
                        <p className="note-indispo">Demande {s.statut} — ré-émettez un lien si la personne est là.</p>
                      )
                    )}

                    {/* ── les constats PRÉ-REMPLIS, étiquetés par ce qui les
                        porte — jamais « identité » nue (ligne rouge n° 1) ── */}
                    {!verse && constats.length > 0 && (
                      <p className="mention">
                        Constaté par Mowibo ID ({s!.decision === 'approuve' ? 'approuvé en présence' : 'lecture de pièce — non vérifié'}) :{' '}
                        {constats.map(([k, v]) => `${k} : ${String(v)}`).join(' · ')}
                      </p>
                    )}

                    {/* ── les gestes de l'étape ── */}
                    {/* ⚠️ `e.lien` EST NUL POUR UN RATTACHEMENT : l'afficher
                        rendrait « null » dans un bloc intitulé « le lien ». */}
                    {!verse && e && e.lien && s?.decision !== 'approuve' && (
                      <details className="versement-lien">
                        <summary>Le lien d'enrôlement (à ouvrir sur le téléphone)</summary>
                        <code>{e.lien}</code>
                      </details>
                    )}
                    {x.titulaire && !verse && (
                      <button type="button" className="lien-action" disabled={enCours}
                              onClick={() => void demanderPourPartie(x)}>
                        Demander son identité — {x.libelle}
                      </button>
                    )}
                    {!verse && !e && (
                      <button type="button" className="lien-action" disabled={enCours}
                              onClick={() => void enrolerPartie(x)}>
                        Enrôler au guichet (sans compte Mowibo ID)
                      </button>
                    )}
                    {!verse && e && s && s.decision !== 'approuve' && (s.statut === 'expiree' || s.statut === 'annulee') && (
                      <button type="button" className="lien-action" disabled={enCours}
                              onClick={() => void enrolerPartie(x)}>
                        Ré-émettre un lien d'enrôlement
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* ── DÉCLARER UNE PARTIE — « la pièce d'abord » : le rôle vient du
              vocabulaire des actes, le nom est facultatif (il viendra de la
              pièce), et le geste émet aussitôt le lien d'enrôlement. ── */}
          {parties && (
            <div className="versement-demande">
              <label>
                Déclarer une partie — son rôle dans l'acte
                <select value={ajoutRole} onChange={(ev) => setAjoutRole(ev.target.value)}>
                  <option value="">— choisir un rôle —</option>
                  {rolesDeclarables.map((r) => (
                    <option key={r.role} value={r.role}>{r.libelle}</option>
                  ))}
                </select>
              </label>
              {/* ── LA PIÈCE D'ABORD : on CHERCHE avant de CRÉER ──────────
                  Proposition retenue le 14/10, ligne rouge n° 3. Deux défauts
                  fermés d'un coup : la personne déjà inscrite ne refait pas sa
                  capture, et elle ne reçoit pas une SECONDE fiche d'état civil
                  — deux titulaires pour une personne, dans un registre
                  FONCIER. ── */}
              <label>
                Sa carte d'identité ? — son numéro (NIN)
                <input value={nin} onChange={(ev) => setNin(ev.target.value)}
                       autoComplete="off" inputMode="numeric" spellCheck={false}
                       placeholder="les chiffres portés sur la carte" />
              </label>
              <button type="button" className="lien-action" disabled={enCours || !nin.trim()}
                      onClick={() => void reconnaitre()}>
                Chercher au registre avant de créer
              </button>
              <p className="mention">
                Le numéro n'est pas conservé : il sert à cette seule recherche, le champ
                se vide, et le registre n'en garde qu'une empreinte et les quatre
                derniers chiffres.
              </p>

              {reco && (
                <div className="versement-canaux">
                  <p className={reco.verdict === 'inconnu_a_enroler' ? 'attente' : 'mention'}>
                    {reco.aDire}
                  </p>
                  {/* ⚠️ L'ÉTAT DU NIN S'AFFICHE EN ENTIER — vérifié · déclaré ·
                      absent. Une fiche retrouvée ne prouve PAS que l'identité a
                      été constatée (ligne rouge n° 1 : pré-rempli ≠ vérifié). */}
                  {reco.etatNin && <p className="mention">{reco.etatNin}</p>}
                  {(reco.verdict === 'rattache_existant' || reco.verdict === 'rattache_puis_enrole') && (
                    <>
                      {!ajoutRole && (
                        <p className="attente">
                          Choisissez son rôle dans l'acte, ci-dessus, pour la rattacher.
                        </p>
                      )}
                      <button type="button" className="lien-action"
                              disabled={enCours || !ajoutRole}
                              onClick={() => void rattacherReconnu()}>
                        {reco.verdict === 'rattache_existant'
                          ? 'Rattacher sans re-capture'
                          : 'Rattacher sa fiche, puis enrôler'}
                      </button>
                    </>
                  )}
                </div>
              )}

              <label>
                Nom (facultatif — il viendra de la pièce)
                <input value={ajoutNom} onChange={(ev) => setAjoutNom(ev.target.value)}
                       placeholder="tel que déclaré, si connu" />
              </label>
              <button type="button" className="lien-action" disabled={enCours || !ajoutRole}
                      onClick={() => void declarerEtEnroler()}>
                Déclarer et enrôler au guichet
              </button>
            </div>
          )}

          {rattachementADire && (
            <div className="versement-canaux">
              <p className="mention">{rattachementADire}</p>
            </div>
          )}

          {enrolement && (
            <div className="versement-canaux">
              <p className="mention">{enrolement.aDire}</p>
              <details className="versement-lien" open>
                <summary>Le lien d'enrôlement (app Mowibo ID, mode agent)</summary>
                <code>{enrolement.lien}</code>
              </details>
            </div>
          )}

          {/* ⚠️ LE CHEMIN MANUEL RESTE ENTIER — décision du fondateur. Replié :
              il sert quand la personne n'apparaît dans aucune source. */}
          <details className="versement-lien">
            <summary>Demander par identifiant de titulaire (chemin manuel)</summary>
            <label>
              Intervenant (identifiant du titulaire)
              <input
                value={titulaireId}
                onChange={(e) => setTitulaireId(e.target.value.trim())}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </label>
            <label>
              Motif — ce que la personne lira sur son écran de consentement
              <input
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Instruction de la vente : identité du cédant"
              />
            </label>
            <button
              type="button"
              disabled={enCours || titulaireId.length < 36 || motif.trim().length < 10}
              onClick={demanderVersement}
            >
              {enCours ? 'Ouverture…' : 'Demander son identité'}
            </button>
          </details>
        </div>
      )}

      {/* ── Les trois canaux, le code d'abord ──────────────────────────── */}
      {demande && (
        <div className="versement-canaux">
          <p className="mention">
            Demande ouverte pour <strong>{demande.titulaire.denomination}</strong>.
          </p>

          {/* ⚠️ LE CODE EN PREMIER ET EN GRAND : c'est le canal du poste
              bureautique, qui n'a pas de caméra. */}
          {demande.code ? (
            <p className="versement-code" aria-label="Code à dicter">
              {demande.code.slice(0, 3)}&nbsp;{demande.code.slice(3)}
            </p>
          ) : (
            <p className="note-indispo">
              Aucun code n'a été attribué à cette demande : employez le lien ou le QR.
            </p>
          )}

          <p className="mention">{demande.aDire}</p>

          {demande.qr && (
            <img className="versement-qr" src={demande.qr}
                 alt="QR à présenter au téléphone de la personne" />
          )}

          {/* ⚠️ LE LIEN EST REPLIÉ. Déplié, il reste lisible par-dessus l'épaule
              pendant toute la durée de validité — et c'est un consentement
              contournable. On le montre à qui le demande. */}
          <details className="versement-lien">
            <summary>Envoyer un lien par courriel</summary>
            <code>{demande.lien}</code>
          </details>

          <button type="button" disabled={enCours} onClick={() => recevoir(demande.id)}>
            {enCours ? 'Lecture…' : "La personne a autorisé — recevoir l'identité"}
          </button>
        </div>
      )}

      {/* ── Ce qui a été reçu ──────────────────────────────────────────── */}
      {recu && (
        <div className="versement-recu">
          {/* ⚠️ ENCADRÉ, parce que c'est le SEUL contrôle humain : rien ne
              prouve que c'est bien cette personne qui a autorisé. */}
          <p className="versement-nom-verse">{recu.denomination}</p>
          <ul className="fiche-comptes">
            <li>Numéro national : <strong>{recu.ninRattache ? 'rattaché' : 'non versé'}</strong></li>
            <li>Pièces reçues : <strong>{recu.piecesVersees.length}</strong>
              {recu.piecesVersees.length > 0 && ` (${recu.piecesVersees.join(', ')})`}</li>
          </ul>
          {recu.avertissement && (
            <p className="alerte-trace" role="alert">⚠ {recu.avertissement}</p>
          )}
          {recu.piecesIllisibles.map((p) => (
            <p key={p.cle} className="note-indispo">{p.cle} : {p.motif}</p>
          ))}
        </div>
      )}

      {/* ── L'historique de l'affaire ──────────────────────────────────── */}
      {lignes && lignes.length > 0 && (
        <ul className="liste-parties">
          {lignes.map((l) => (
            <li key={l.id}>
              {l.denomination} — <strong>{l.statut}</strong>
              {' · '}{l.pieces} pièce(s)
              {l.statut === 'demande' && (
                <button type="button" className="lien-action" disabled={enCours}
                        onClick={() => recevoir(l.id)}>recevoir</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {lignes && lignes.length === 0 && (
        <p className="attente">Aucune identité versée dans cette affaire.</p>
      )}
    </section>
  );
}
